import { isEmpty, isNil } from "lodash-es";
import { IntlShape } from "react-intl";
import { capitalizeWords, humanize, isNothing, isSomething } from "./langUtils";
import ProductRules from "../rules/ProductRules";

const HBM_LABELS_WITH_TRANSLATIONS = new Set([
  "label.value_key_mapping.hbm_buildup_index",
  "label.value_key_mapping.hbm_conductivity",
  "label.value_key_mapping.hbm_hbsi",
  "label.value_key_mapping.hbm_hbsi_status",
  "label.value_key_mapping.hbm_temperature_process",
  "label.value_key_mapping.hbm_carrier_pipe_temp",
]);

export function findPrimaryValue(values: $TSFixMe, doNotUseFirstValue = false) {
  if (!isNil(values) && !isEmpty(values)) {
    let primary = values.find((value: $TSFixMe) => value.key === "level");
    if (!primary) {
      primary = values.find(
        (value: $TSFixMe) => ["pv", "distance"].indexOf(value.key) >= 0,
      );
    }
    if (primary) {
      return primary;
    }
    if (doNotUseFirstValue) {
      // dirty hack for FWR30 if it doesn't send level or distance
      return values.find(
        (value: $TSFixMe) =>
          value.key !== "angle" &&
          value.key !== "temperature" &&
          value.key !== "signal_quality" &&
          value.key !== "battery" &&
          value.key !== "rssi",
      );
    }
    return values[0];
  }
  return undefined;
}

export function findValueByKey(values: $TSFixMe, key: $TSFixMe) {
  if (!isNil(values) && !isEmpty(values) && !isNil(key)) {
    return values.find((value: $TSFixMe) => value.key === key);
  }
  return undefined;
}

export function addUnitToValue(
  value: $TSFixMe,
  unit: $TSFixMe,
  defaultNoValue: $TSFixMe,
) {
  if (!value || value === "NaN") {
    return defaultNoValue;
  }
  return `${value}${unit && unit.name ? unit.name : ""}`;
}

export function convertAssetValues(
  asset: $TSFixMe,
  values: $TSFixMe,
  rules: $TSFixMe,
) {
  if (isNothing(values)) return values;
  if (values.length > 0 && asset.systems) {
    // filter values
    if (
      asset.isMebakActivated &&
      rules.asset(asset).get("defaultToMebakKeyMapping")
    ) {
      const mebakKeys = rules.asset(asset).get("defaultToMebakKeyMapping");
      let filteredValues: $TSFixMe = [];
      Object.values(mebakKeys).forEach((key) => {
        filteredValues.push(values.find((item: $TSFixMe) => item.key === key));
      });
      // @ts-expect-error TS(7006) FIXME: Parameter 'item' implicitly has an 'any' type.
      filteredValues = filteredValues.filter((item) => item !== undefined);
      return filteredValues;
    }
    const system = asset.systems[0];
    if (system && system.keyValueMapping) {
      const keyValueMapping = Object.values(system.keyValueMapping).filter(
        (item) => isSomething(item) && item !== "empty",
      );
      if (keyValueMapping.length > 0) {
        let filteredValues: $TSFixMe = [];
        keyValueMapping.forEach((key) => {
          // This pushes also undefined values into the array, therefore they should be filtered afterwards
          filteredValues.push(
            values.find((item: $TSFixMe) => item.key === key),
          );
        });
        // @ts-expect-error TS(7006) FIXME: Parameter 'item' implicitly has an 'any' type.
        filteredValues = filteredValues.filter((item) => item !== undefined);

        // @ts-expect-error TS(7006) FIXME: Parameter 'v' implicitly has an 'any' type.
        filteredValues.forEach((v) => {
          if (typeof v.unit === "object") {
            // eslint-disable-next-line no-param-reassign
            v.unit = v.unit.symbol;
          }
        });
        return filteredValues;
      }
    }
  }
  if (values.length > 0 && asset.instrumentations?.items) {
    const convertedValues: $TSFixMe = [];
    const instrumentation = asset.instrumentations?.items?.find((i: $TSFixMe) =>
      isSomething(i.specifications),
    );
    const instrumentationUnit =
      instrumentation && instrumentation?.specifications["eh.user_config.unit"]
        ? {
            name: instrumentation.specifications["eh.user_config.unit"]?.value,
            symbol: instrumentation.specifications["eh.user_config.unit"].value,
          }
        : undefined;
    values.forEach((value: $TSFixMe) => {
      let convertedValue = value;
      if (instrumentationUnit) {
        const ruleValue = rules
          .asset(asset)
          ?.get("convertDistanceValue", instrumentationUnit, value);
        convertedValue = ruleValue || value;
      }
      if (typeof convertedValue.unit === "object") {
        // hack to provide the unit in correct format for AssetValueTile and AssetDetailsValueLatest
        // necessary due to different loading / handling of the parent components
        convertedValue.unit = convertedValue.unit.symbol;
      }
      convertedValues.push(convertedValue);
    });
    return convertedValues;
  }
  values.forEach((v: $TSFixMe) => {
    if (typeof v.unit === "object") {
      // eslint-disable-next-line no-param-reassign
      v.unit = v.unit.symbol;
    }
  });
  return values;
}

export function getOriginalDisplayLabel(
  asset: $TSFixMe,
  key: $TSFixMe,
  rules: $TSFixMe,
  intl: $TSFixMe,
  instrumentationRules = undefined,
) {
  const productRules = asset
    ? ProductRules.find(
        asset.product?.manufacturer?.name,
        asset.product?.product_code,
        asset.product?.tenant?.public,
      )
    : undefined;

  const assetRules = asset ? rules?.asset(asset) : undefined;
  const assetRulesName = assetRules?.get("nameForValueKey", key);

  const hasHBMTranslation =
    rules?.application?.().get("hasMonitoringValueKeyTranslation") &&
    HBM_LABELS_WITH_TRANSLATIONS.has(`label.value_key_mapping.${key}`);

  const translationTarget = assetRulesName ?? key;
  const hasTranslation =
    productRules?.get("hasValueKeyTranslation", key) || hasHBMTranslation;

  if (hasTranslation) {
    return intl?.formatMessage({
      id: `label.value_key_mapping.${translationTarget}`,
    });
  }

  const instrumentationRulesName = (instrumentationRules as $TSFixMe)?.get(
    "nameForValueKey",
    key,
  );

  const name = instrumentationRulesName ?? assetRulesName ?? key;
  return capitalizeWords(humanize(name));
}

export function getDisplayLabel(
  asset: $TSFixMe,
  key: $TSFixMe,
  rules: $TSFixMe,
  intl: $TSFixMe,
  instrumentationRules = undefined,
) {
  const valueWithUnit = asset?.values?.find((v: $TSFixMe) => v.key === key);
  const customDisplayLabel = valueWithUnit?.display_label;

  const showCustomDisplayLabel = rules
    ?.application?.()
    ?.get("shouldShowCustomDisplayLabelForValues");

  if (customDisplayLabel && showCustomDisplayLabel) {
    return customDisplayLabel;
  }
  return getOriginalDisplayLabel(asset, key, rules, intl, instrumentationRules);
}

export function convertValuesIntoInstrumentationUnit(
  asset: $TSFixMe,
  values: $TSFixMe,
  rules: $TSFixMe,
  intl = undefined,
) {
  if (isNothing(values)) return values;
  if (values.length > 0 && asset.instrumentations) {
    const convertedValues: $TSFixMe = [];
    const instrumentation = asset.instrumentations.find((i: $TSFixMe) =>
      isSomething(i.unit),
    );
    const instrumentationUnit = instrumentation
      ? {
          name: instrumentation.unit,
          symbol: instrumentation.unit,
        }
      : undefined;
    values.forEach((value: $TSFixMe) => {
      let convertedValue = value;
      if (instrumentationUnit) {
        const ruleValue = rules
          .asset(asset)
          ?.get("convertDistanceValue", instrumentationUnit, value);
        convertedValue = ruleValue || value;
      }
      convertedValue = {
        ...convertedValue,
        displayedLabel: getDisplayLabel(asset, value.key, rules, intl),
      };
      if (typeof convertedValue.unit === "object") {
        // hack to provide the unit in correct format for AssetValueTile and AssetDetailsValueLatest
        // necessary due to different loading loading / handling of the parent components
        convertedValue.unit = convertedValue.unit.symbol;
      }
      convertedValues.push(convertedValue);
    });
    return convertedValues;
  }
  values.forEach((v: $TSFixMe) => {
    if (typeof v.unit === "object") {
      // eslint-disable-next-line no-param-reassign
      v.unit = v.unit.symbol;
    }
  });
  return values;
}

export function filterSpecification(
  specificationString: $TSFixMe,
  specifications: $TSFixMe,
) {
  return specifications
    ? Object.entries(specifications).filter((specification) =>
        specification[0].includes(specificationString),
      )
    : null;
}

export function computeRoundedValue(
  value: number,
  intl: IntlShape,
  minFractionDigits: number = 0,
  maxFractionDigits: number = 3,
) {
  const safeMinFractionDigits = Math.min(20, Math.max(0, minFractionDigits));
  const safeMaxFractionDigits = Math.min(
    20,
    Math.max(safeMinFractionDigits, maxFractionDigits),
  );
  let formattedValue = intl.formatNumber(value, {
    minimumFractionDigits: safeMinFractionDigits,
    maximumFractionDigits: safeMaxFractionDigits,
  });

  if (formattedValue.length > 10) {
    formattedValue = intl.formatNumber(value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    if (formattedValue.length > 10) {
      formattedValue = `${formattedValue.slice(0, 10)}..`;
    }
  }
  return formattedValue;
}
